import apiClient from './axios';
import { ApiResponse } from '../types';

/**
 * Every business on PayIQ, and everything about one.
 *
 * Read-only throughout. Tier decisions go through the KYB review queue,
 * which records who decided and why; money moves through its own paths.
 * There is deliberately no endpoint here that edits a company's registration
 * details — that would be a way to change what a compliance record says
 * after the fact.
 */

export interface BusinessRow {
  id: string;
  name: string;
  tradingName: string | null;
  rcNumber: string | null;
  type: string;
  industry: string | null;
  email: string | null;
  phone: string | null;
  kybStatus: 'unverified' | 'pending' | 'verified' | 'rejected';
  kybTier: number;
  balance: number;
  /** Their own account number, or null if no director has verified yet. */
  accountNumber: string | null;
  /**
   * Whose name the account is in.
   *
   * `individual` was opened against the director's BVN at Tier 1 and carries
   * their name on every transfer out; `corporate` was opened against the CAC
   * registration and carries the company's. Null means no account — the
   * business cannot collect or send at all.
   */
  accountKind: 'individual' | 'corporate' | null;
  createdAt: string;
}

export interface BusinessDetail {
  registration: {
    id: string;
    name: string;
    tradingName: string | null;
    slug: string;
    type: string;
    industry: string | null;
    sizeBand: string | null;
    purposes: string[] | null;
    rcNumber: string | null;
    tin: string | null;
    registeredAddress: string | null;
    email: string | null;
    phone: string | null;
    contactName: string | null;
    kybStatus: string;
    kybTier: number;
    createdAt: string;
  };
  /**
   * The credential that registered this business.
   *
   * A business belongs to exactly one login, so "which email do you sign in
   * with" has an answer that does not involve reading the team list and
   * guessing which member row is the owner.
   *
   * Null only when that login is gone, which cascades — in practice it
   * means something is wrong.
   */
  owner: {
    id: string;
    email: string;
    phone: string | null;
    fullName: string | null;
    lastLoginAt: string | null;
    /** Set while sign-in is refusing. Support can see it rather than
     *  reproducing it with a failed attempt. */
    lockedUntil: string | null;
  } | null;
  /** The ladder, straight from the same service the app reads — so support
   *  and the customer never see two answers to "what's outstanding?". */
  kyb: {
    currentTier: number;
    tiers: {
      tier: number;
      name: string;
      satisfied: boolean;
      requirements: { key: string; label: string; status: string }[];
    }[];
  } | null;
  directors: {
    id: string;
    fullName: string;
    boardRole: string | null;
    /**
     * Whether they hold a seat.
     *
     * The whole of the director/shareholder distinction now that the
     * register records no percentage: being on it *is* the declaration of
     * significant control, so there is nothing left to threshold.
     */
    isDirector: boolean;
    isSignatory: boolean;
    email: string | null;
    phone: string | null;
    dateOfBirth: string | null;
    /** Theirs, not the company's registered address. */
    residentialAddress: string | null;
    /** Last 4 only — see the service. */
    bvnLast4: string | null;
    idType: string | null;
    idLast4: string | null;
    safehavenVerifiedAt: string | null;
    /** The BVN step per provider. Optional for servers that predate it. */
    bvn?: {
      dojah: {
        /** Which service ran the lookup — `dojah`, `interswitch`, `manual`. */
        provider?: string | null;
        status: 'missing' | 'pending' | 'verified' | 'failed' | 'expired' | string;
        verifiedAt: string | null;
        expiresAt: string | null;
        failureReason: string | null;
      };
      safehaven: 'verified' | 'code_sent' | 'not_started';
    };
  }[];
  team: {
    id: string;
    userId: string;
    name: string;
    email: string | null;
    jobTitle: string | null;
    status: string;
    joinedAt: string | null;
  }[];
  money: {
    /**
     * Spendable, and the same figure the app shows — the bank's balance for
     * this account less what is already committed. Read through the same
     * service the app reads, so a support agent and a customer looking at
     * their phone are never quoting different numbers at each other.
     */
    balance: number;
    /** `bank` when the provider answered, `unavailable` when it didn't and
     *  the ledger stood in. An agent needs to know which they are quoting. */
    balanceSource: 'bank' | 'unavailable';
    /** What the bank holds, before pending debits are taken off. */
    settledBalance: number;
    /** Money already on its way out, or held awaiting approval. */
    pendingOut: number;
    account: {
      accountNumber: string;
      accountName: string | null;
      bankName: string;
      status: string;
      /** Whose name it is in. See `accountKind` on the list row. */
      kind: 'individual' | 'corporate';
      openedAt: string;
    } | null;
  };
  paymentRequests: {
    id: string;
    status: string;
    amount: number;
    amountPaid: number;
    customerName: string | null;
    settlesTo: string;
    createdAt: string;
  }[];
  /**
   * The CAC filing as it was read when this company was claimed.
   *
   * Null for a business that never claimed a registration — and for one
   * claimed through the Dojah fallback, which returns no company id and so
   * has no filing to come back to.
   *
   * `fetchedAt` is the point of it. A filing read three months ago is
   * evidence about three months ago, and a reviewer deciding a held claim
   * has to see that rather than assume it is current.
   */
  cacRegister: {
    rcNumber: string;
    approvedName: string;
    companyType: string | null;
    registrationDate: string | null;
    /** The register's own word for whether the company still exists. A
     *  struck-off company stays findable forever. */
    isActive: boolean;
    fetchedAt: string;
    providerCompanyId: string;
    /** Derived with the same function the claim and Tier 2 use, so all
     *  three agree on who controls the company. */
    directors: {
      fullName: string;
      boardRole: string | null;
      email: string | null;
      phone: string | null;
      dateOfBirth: string | null;
      nationality: string | null;
    }[];
    /** The register's rows, uninterpreted. Nothing downstream reads them,
     *  and a reviewer asking what it said should get what it said. */
    shareholders: unknown[];
  } | null;

  transactions: {
    id: string;
    type: 'credit' | 'debit';
    status: string;
    amount: number;
    counterparty: string | null;
    narration: string | null;
    reference: string;
    createdAt: string;
  }[];
}

export interface PagedBusinesses {
  items: BusinessRow[];
  total: number;
  page: number;
  limit: number;
}

export const businessesApi = {
  list: async (
    params: {
      search?: string;
      kybStatus?: string;
      tier?: number;
      withoutAccount?: boolean;
      page?: number;
      limit?: number;
    } = {},
  ) => {
    const { data } = await apiClient.get<ApiResponse<PagedBusinesses>>(
      '/admin/businesses',
      { params },
    );
    return data.data;
  },

  get: async (id: string) => {
    const { data } = await apiClient.get<ApiResponse<BusinessDetail>>(
      `/admin/businesses/${id}`,
    );
    return data.data;
  },
};
